import Polygon from 'jsts/org/locationtech/jts/geom/Polygon';
import GeometryFactory from 'jsts/org/locationtech/jts/geom/GeometryFactory';
import MultiPolygon from 'jsts/org/locationtech/jts/geom/MultiPolygon';
import Coordinate from 'jsts/org/locationtech/jts/geom/Coordinate';
import OverlayOp from 'jsts/org/locationtech/jts/operation/overlay/OverlayOp';
import Geometry from 'jsts/org/locationtech/jts/geom/Geometry';
import {
  MultiPolygon2,
  MultiPolygon2Result,
  ResultType,
} from '../../geometric-types';
import { toJstsMultiPolygon, toMultiPolygon2 } from '../util/polygon';
import { calculateBoundingBox } from '../sheet-shape-corner';

function sanitizeCoordinates(raw: Coordinate[]): Coordinate[] {
  const deduped: Coordinate[] = [];
  for (let i = 0; i < raw.length; i++) {
    const c = raw[i];
    const prev = deduped.length ? deduped[deduped.length - 1] : null;
    if (!prev || c.x !== prev.x || c.y !== prev.y) {
      deduped.push(c);
    }
  }
  if (deduped.length > 0) {
    const first = deduped[0];
    const last = deduped[deduped.length - 1];
    if (first.x !== last.x || first.y !== last.y) {
      deduped.push(first);
    }
  }
  return deduped;
}

function generatePoints(
  extendedStart: Coordinate,
  extendedEnd: Coordinate,
  perpendicular: Coordinate,
  widthBuffer: number
) {
  return [
    new Coordinate(
      extendedStart.x + perpendicular.x * widthBuffer,
      extendedStart.y + perpendicular.y * widthBuffer
    ),
    new Coordinate(
      extendedEnd.x + perpendicular.x * widthBuffer,
      extendedEnd.y + perpendicular.y * widthBuffer
    ),
    new Coordinate(
      extendedEnd.x - perpendicular.x * widthBuffer,
      extendedEnd.y - perpendicular.y * widthBuffer
    ),
    new Coordinate(
      extendedStart.x - perpendicular.x * widthBuffer,
      extendedStart.y - perpendicular.y * widthBuffer
    ),
    new Coordinate(
      extendedStart.x + perpendicular.x * widthBuffer,
      extendedStart.y + perpendicular.y * widthBuffer
    ), // closing the ring
  ];
}

function createBufferFromSegment(
  vertex: Coordinate,
  nextVertex: Coordinate,
  widthBuffer: number
): Polygon[] {
  const geomFactory = new GeometryFactory();

  const direction = new Coordinate(
    nextVertex.x - vertex.x,
    nextVertex.y - vertex.y
  );
  const lengthNorm = Math.sqrt(
    direction.x * direction.x + direction.y * direction.y
  );
  // Skip degenerate segments to avoid invalid rings
  if (!isFinite(lengthNorm) || lengthNorm === 0) {
    return [];
  }
  const unitDirection = new Coordinate(
    direction.x / lengthNorm,
    direction.y / lengthNorm
  );

  const perpendicular = new Coordinate(-unitDirection.y, unitDirection.x);

  const extendedStart = new Coordinate(
    vertex.x - unitDirection.x * widthBuffer,
    vertex.y - unitDirection.y * widthBuffer
  );

  const extendedEnd = new Coordinate(
    nextVertex.x + unitDirection.x * widthBuffer,
    nextVertex.y + unitDirection.y * widthBuffer
  );

  const points1 = generatePoints(
    extendedStart,
    extendedEnd,
    perpendicular,
    widthBuffer
  );

  let arrayBuffers = [points1];

  return arrayBuffers.map((point) =>
    geomFactory.createPolygon(
      geomFactory.createLinearRing(point).getCoordinates()
    )
  );
}

function createBuffersAlongEdges(
  linearRing: Geometry,
  widthBuffer: number
): MultiPolygon {
  const geomFactory = new GeometryFactory();
  const polygons: Polygon[] = [];

  const coordinates: Coordinate[] = sanitizeCoordinates(
    linearRing.getCoordinates()
  );

  // LinearRing must have at least 4 coordinates (including closure)
  if (coordinates.length < 4) {
    return geomFactory.createMultiPolygon([]);
  }

  for (let j = 0; j < coordinates.length - 1; j++) {
    const bufferPolygon = createBufferFromSegment(
      coordinates[j],
      coordinates[j + 1],
      widthBuffer
    );
    polygons.push(...bufferPolygon);
  }

  for (let j = 1; j < coordinates.length; j++) {
    const bufferPolygon = createBufferFromSegment(
      coordinates[j],
      coordinates[j - 1],
      widthBuffer
    );
    polygons.push(...bufferPolygon);
  }

  return geomFactory.createMultiPolygon(polygons);
}

// eslint-disable-next-line complexity
export function calculateSheetShapeRandfelt(
  multiPolygonInput: MultiPolygon2,
  roofHeight: number | undefined,
  unitScale: number
): MultiPolygon2Result {
  const jstsMultiPolygon = toJstsMultiPolygon(multiPolygonInput);
  const geomFactory = new GeometryFactory();

  let e = calculateBoundingBox(jstsMultiPolygon, unitScale);

  //TPF Number 5. e is minimum of b and 2H
  if (roofHeight) e = Math.min(e, 2 * roofHeight);

  e = e / 10;

  const widthBufferScaled = e / unitScale;

  const polygons: Polygon[] = [];

  for (let i = 0; i < jstsMultiPolygon.getNumGeometries(); i++) {
    const polygon = jstsMultiPolygon.getGeometryN(i);

    const exteriorRing = polygon.getExteriorRing();
    const outerBuffer = createBuffersAlongEdges(
      exteriorRing,
      widthBufferScaled
    );
    for (let j = 0; j < outerBuffer.getNumGeometries(); j++) {
      polygons.push(outerBuffer.getGeometryN(j));
    }
  }

  let segmentsPolygons = geomFactory.createMultiPolygon(polygons);

  let finalMultiPolygon: MultiPolygon = jstsMultiPolygon;

  for (let i = 0; i < segmentsPolygons.getNumGeometries(); i++) {
    const newPolygons: Polygon[] = [];

    for (let ii = 0; ii < finalMultiPolygon.getNumGeometries(); ii++) {
      const polygon = finalMultiPolygon.getGeometryN(ii) as Polygon;
      const difference = OverlayOp.difference(
        polygon,
        segmentsPolygons.getGeometryN(i)
      );

      if (difference instanceof Polygon) {
        newPolygons.push(difference);
      } else if (difference instanceof MultiPolygon) {
        for (let j = 0; j < difference.getNumGeometries(); j++) {
          newPolygons.push(difference.getGeometryN(j) as Polygon);
        }
      }
    }
    finalMultiPolygon = geomFactory.createMultiPolygon(newPolygons);
  }

  if (finalMultiPolygon) {
    const theFinalFinal = OverlayOp.difference(
      jstsMultiPolygon,
      finalMultiPolygon
    );

    const value = theFinalFinal.getArea();
    const multiPolygon = toMultiPolygon2(theFinalFinal);

    return {
      type: ResultType.MULTIPOLYGON2,
      value,
      multiPolygon,
    };
  } else {
    throw new Error('finalMultiPolygon is undefined');
  }
}
